import React, { useState } from 'react';
import { ShieldAlert, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Persistent amber banner shown at the very top of every page whenever an
 * Ox Resin staff member is acting as an installer.
 *
 * Reads `user.impersonator` from AuthContext (set by /api/auth/me when the
 * session was minted by /api/staff/impersonate). Clicking "Exit and return to
 * staff" calls /api/staff/stop-impersonating and restores the original staff
 * session in place.
 */
export default function ImpersonationBanner() {
  const { user, isImpersonating, impersonator, stopImpersonating } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating || !impersonator) return null;

  const handleExit = async () => {
    setExiting(true);
    try {
      await stopImpersonating();
      toast.success(`Restored ${impersonator.name || 'staff'} session.`);
    } catch (err) {
      toast.error(err.message || 'Could not exit impersonation.');
    } finally {
      setExiting(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 bg-amber-500 text-amber-950 border-b-2 border-amber-700 shadow-md"
    >
      <div className="max-w-6xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span>
            <span className="font-semibold">Acting as</span>{' '}
            <span className="font-mono font-bold">{user?.name}</span>
            {user?.account_code && (
              <>
                {' '}<span className="font-mono">({user.account_code})</span>
              </>
            )}
            {' '}— signed in as{' '}
            <span className="font-semibold">{impersonator.name}</span>{' '}
            <span className="text-xs uppercase tracking-wider opacity-80">({impersonator.role})</span>
          </span>
        </div>
        <Button
          onClick={handleExit}
          disabled={exiting}
          data-testid="impersonation-banner-exit-btn"
          size="sm"
          variant="outline"
          className="bg-white/80 hover:bg-white border-amber-700 text-amber-900 font-semibold"
        >
          <LogOut className="w-4 h-4 mr-1" />
          {exiting ? 'Exiting…' : 'Exit and return to staff'}
        </Button>
      </div>
    </div>
  );
}
